import { useEffect } from "react";
import { api } from "../lib/api";

/**
 * Loads enabled tracking snippets from GET /api/tracking and injects each one
 * into <head>, right after <body>, or right before </body> based on `placement`.
 * Only runs once per page load. Removes previously-injected snippets on unmount.
 */
export default function TrackingLoader() {
  useEffect(() => {
    const attr = "data-shaan-tracking";
    let cancelled = false;

    api.get("/tracking")
      .then(({ data }) => {
        if (cancelled) return;
        data.forEach((snippet) => injectSnippet(snippet, attr));
      })
      .catch(() => {
        /* silent — tracking is a nice-to-have, never block the page */
      });

    return () => {
      cancelled = true;
      document.querySelectorAll(`[${attr}]`).forEach((n) => n.remove());
    };
  }, []);

  return null;
}

function injectSnippet(snippet, attr) {
  // Parse the code as HTML fragment
  const tmp = document.createElement("template");
  tmp.innerHTML = snippet.code.trim();
  const nodes = Array.from(tmp.content.childNodes);
  if (nodes.length === 0) return;

  const target =
    snippet.placement === "head"
      ? document.head
      : document.body;

  nodes.forEach((node) => {
    // <script> nodes created via innerHTML aren't executed by browsers;
    // clone them into real script elements so they DO execute.
    if (node.tagName === "SCRIPT") {
      const s = document.createElement("script");
      Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.text = node.textContent || "";
      s.setAttribute(attr, snippet.id);
      target.appendChild(s);
      return;
    }
    if (node.nodeType === 1) {
      node.setAttribute(attr, snippet.id);
    }
    if (snippet.placement === "body-start" && target.firstChild) {
      target.insertBefore(node, target.firstChild);
    } else {
      target.appendChild(node);
    }
  });
}
